import { useCallback, useEffect, useMemo, useState } from 'react'
import { useReactTable, getCoreRowModel, ColumnDef, flexRender } from '@tanstack/react-table'
import { toast } from 'sonner'
import { usePortal } from '@/contexts/PortalContext'
import type { ExtractionResult } from '@/lib/portalApi'

function getConfidenceBadgeClass(confidence: number): string {
  if (confidence >= 0.85) return 'bg-green-100 text-green-800'
  if (confidence >= 0.60) return 'bg-amber-100 text-amber-800'
  return 'bg-red-100 text-red-800'
}

export function AIExtractionTable() {
  const { extractions, acceptExtraction, rejectExtraction, data } = usePortal()

  // Valeurs éditées inline — initialisées depuis les extractions (piège #1 : lazy init)
  const [localValues, setLocalValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(extractions.map(e => [e.id, e.extractedValue]))
  )

  // État visuel des lignes
  const [confirmedIds, setConfirmedIds] = useState<Set<string>>(new Set())
  const [rejectedIds, setRejectedIds] = useState<Set<string>>(new Set())
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set())

  // Sync avec les extractions (re-upload ou changement) — piège #1
  useEffect(() => {
    setLocalValues(Object.fromEntries(extractions.map(e => [e.id, e.extractedValue])))
    setConfirmedIds(new Set())
    setRejectedIds(new Set())
    setLoadingIds(new Set())
  }, [extractions])

  // Lookup fieldId → label depuis le template (piège #3)
  const fieldLabelMap = useMemo(() => {
    const map = new Map<string, string>()
    data?.template.sections.forEach(section => {
      section.fields.forEach(field => {
        map.set(field.id, field.label)
      })
    })
    return map
  }, [data])

  const handleConfirm = useCallback(async (id: string) => {
    setLoadingIds(prev => new Set([...prev, id]))
    try {
      await acceptExtraction(id, localValues[id] ?? extractions.find(e => e.id === id)?.extractedValue ?? '')
      setConfirmedIds(prev => new Set([...prev, id]))
    } catch (e: unknown) {
      toast.error('Erreur lors de la confirmation. Veuillez réessayer.', { duration: Infinity })
    } finally {
      setLoadingIds(prev => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }, [acceptExtraction, extractions, localValues])

  const handleReject = useCallback((id: string, fieldId: string) => {
    rejectExtraction(id, fieldId)
    setRejectedIds(prev => new Set([...prev, id]))
  }, [rejectExtraction])

  const columns = useMemo<ColumnDef<ExtractionResult>[]>(() => [
    {
      id: 'fieldLabel',
      header: 'Champ',
      cell: ({ row }) => (
        <span className="text-sm font-medium">
          {fieldLabelMap.get(row.original.fieldId) ?? row.original.fieldId}
        </span>
      ),
    },
    {
      id: 'localValue',
      header: 'Valeur extraite',
      cell: ({ row }) => {
        const isReviewed = confirmedIds.has(row.original.id) || rejectedIds.has(row.original.id)
        if (rejectedIds.has(row.original.id)) {
          return (
            <span className="text-sm text-muted-foreground italic">
              À remplir manuellement
            </span>
          )
        }
        return (
          <input
            type="text"
            className="border rounded px-2 py-1 text-sm w-full min-h-[36px] disabled:bg-muted disabled:text-muted-foreground"
            value={localValues[row.original.id] ?? row.original.extractedValue}
            onChange={e =>
              setLocalValues(prev => ({ ...prev, [row.original.id]: e.target.value }))
            }
            disabled={isReviewed}
            aria-label={`Valeur extraite pour ${fieldLabelMap.get(row.original.fieldId) ?? row.original.fieldId}`}
          />
        )
      },
    },
    {
      id: 'documentUrl',
      header: 'Source',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground truncate max-w-[120px] block" title={row.original.documentUrl}>
          {row.original.documentUrl}
        </span>
      ),
    },
    {
      id: 'confidence',
      header: 'Confiance',
      cell: ({ row }) => {
        const pct = Math.round(row.original.confidence * 100)
        const cls = getConfidenceBadgeClass(row.original.confidence)
        return (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>
            {pct}%
          </span>
        )
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const id = row.original.id
        const isConfirmed = confirmedIds.has(id)
        const isRejected = rejectedIds.has(id)
        const isLoading = loadingIds.has(id)

        if (isConfirmed) {
          return (
            <span className="text-xs text-green-700 font-medium">✓ Confirmé</span>
          )
        }
        if (isRejected) {
          return (
            <span className="text-xs text-red-700 font-medium">✗ Rejeté</span>
          )
        }

        return (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void handleConfirm(id)}
              disabled={isLoading}
              className="text-xs px-2 py-1 rounded bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? '…' : 'Confirmer'}
            </button>
            <button
              type="button"
              onClick={() => handleReject(id, row.original.fieldId)}
              disabled={isLoading}
              className="text-xs px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Rejeter
            </button>
          </div>
        )
      },
    },
  ], [confirmedIds, rejectedIds, loadingIds, localValues, fieldLabelMap, handleConfirm, handleReject])

  const table = useReactTable({
    data: extractions,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className="overflow-x-auto rounded border">
      <table className="w-full text-sm">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id} className="bg-muted/50">
              {headerGroup.headers.map(header => (
                <th key={header.id} className="px-3 py-2 text-left font-medium text-muted-foreground">
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr
              key={row.id}
              className={[
                'border-t transition-colors',
                confirmedIds.has(row.original.id) ? 'bg-green-50/50' : '',
                rejectedIds.has(row.original.id) ? 'bg-red-50/50' : '',
              ].join(' ')}
            >
              {row.getVisibleCells().map(cell => (
                <td key={cell.id} className="px-3 py-2">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
